<template>
  <form v-if="!showThankyou" :class="$style.container">
    <div class="columns">
      <div class="column">
        <FormSelect
          v-model.trim="v$.formData.fair.$model"
          :disabled="false"
          :error="v$.formData.fair.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.fair.errorMessage"
          :initial-option="initialValue"
          :label="config.labels.fair.label"
          :options="selectOptionsPlain"
        />
      </div>
    </div>
    <div v-if="!currentFair.minimized" class="columns">
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.company.$model"
          :bold-label="true"
          :error="v$.formData.company.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.company.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.company.label"
          :original-value="
            previousData.company && previousData.company !== '' ? previousData.company : null
          "
        />
      </div>
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.department.$model"
          :bold-label="true"
          :error="v$.formData.department.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.department.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.department.label"
          :original-value="
            previousData.department && previousData.department !== ''
              ? previousData.department
              : null
          "
        />
      </div>
    </div>
    <div v-if="!currentFair.minimized" class="columns">
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.streetNr.$model"
          :bold-label="true"
          :error="v$.formData.streetNr.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.streetNr.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.streetNr.label"
          :original-value="
            previousData.streetNr && previousData.streetNr !== '' ? previousData.streetNr : null
          "
        />
      </div>
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.zipCity.$model"
          :bold-label="true"
          :error="v$.formData.zipCity.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.zipCity.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.zipCity.label"
          :original-value="
            previousData.zipCity && previousData.zipCity !== '' ? previousData.zipCity : null
          "
        />
      </div>
    </div>
    <div v-if="!currentFair.minimized" class="columns">
      <div class="column">
        <FormInput
          v-model="formData.phone"
          :error="false"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.phone.label"
          :optional-label="config.labels.phone.optionLabel"
          :original-value="
            previousData.phone && previousData.phone !== '' ? previousData.phone : null
          "
        />
      </div>
      <div class="column">
        <FormInput
          v-model="formData.fax"
          :error="false"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.fax.label"
          :optional-label="config.labels.fax.optionLabel"
          :original-value="previousData.fax && previousData.fax !== '' ? previousData.fax : null"
        />
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.email.$model"
          :bold-label="true"
          :error="v$.formData.email.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.email.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.email.label"
          :original-value="
            previousData.email && previousData.email !== '' ? previousData.email : null
          "
        />
      </div>
      <div class="column" />
    </div>
    <div class="columns">
      <div class="column has-text-right-tablet">
        <Button
          colour="blue"
          :is-form="true"
          :text="sendButtonText"
          type="submit"
          @close="handleSubmit"
        />
      </div>
    </div>
    <div v-if="serverError" :class="$style.serverError">
      {{ config.serverErrorMessage }}
    </div>
    <ReCaptcha :execute-switch="executeSwitch" :submit="submit" />
  </form>
</template>

<script setup>
import { email, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import ReCaptcha from '../ReCaptcha/ReCaptcha.vue';
import FormInput from '../FormInput/FormInput.vue';
import FormSelect from '../FormSelect/FormSelect.vue';
import Button from '../Button/Button.vue';
import { computed, onMounted, ref, watch } from 'vue';
import safeFetch from '../../utils/safeFetch';
import { useStoreTranslations } from '../../store/translations/translations';
import { useStoreConfiguration } from '../../store/configuration/configuration';

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

const formData = ref({
  fair: '',
  company: '',
  department: '',
  streetNr: '',
  zipCity: '',
  phone: '',
  fax: '',
  email: '',
  domain: '',
  pageUrl: '',
  token: '',
});
const previousData = ref({});
const isSubmitted = ref(false);
const formSubmitSwitch = ref(false);
const executeSwitch = ref(false);
const showThankyou = ref(false);
const serverError = ref(false);
const currentFair = ref({});

const storeTranslations = useStoreTranslations();
const storeConfig = useStoreConfiguration();

const sendButtonText = computed(() => {
  return storeTranslations.translations?.['forms.button.sent']
    ? storeTranslations.translations?.['forms.button.sent']
    : 'Sent';
});

const rules = computed(() => {
  if (currentFair.value && !currentFair.value.minimized) {
    return {
      formData: {
        fair: {
          required,
        },
        company: {
          required,
        },
        department: {
          required,
        },
        streetNr: {
          required,
        },
        zipCity: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  } else {
    return {
      formData: {
        fair: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  }
});

const v$ = useVuelidate(rules, { formData });

const initialValue = computed(() => {
  let value = '';
  let fairId = getParameter('fair');
  if (!fairId && props.config.initialSelectOption) {
    fairId = props.config.initialSelectOption;
  }
  if (fairId) {
    const preset = props.config.selectOptions.find(function (obj) {
      return obj.id == fairId;
    });
    if (preset) {
      value = preset.name;
    } else {
      value = props.config.selectOptions[0].name;
    }
  } else {
    value = props.config.selectOptions[0].name;
  }
  return value;
});

const selectOptionsPlain = computed(() => {
  return props.config.selectOptions.map((x) => x.name);
});

watch(
  () => formData.value.fair,
  (newFair) => {
    currentFair.value = props.config.selectOptions.find((obj) => {
      return obj.name === newFair;
    });
  },
);

onMounted(() => {
  formData.value.fair = initialValue.value;
});

const handleSubmit = () => {
  serverError.value = false;
  v$.value.$touch();
  isSubmitted.value = true;
  formSubmitSwitch.value = !formSubmitSwitch.value;
  if (!v$.value.$invalid) {
    executeSwitch.value = !executeSwitch.value;
  } else {
    previousData.value = { ...formData.value };
  }
};

const submit = (token) => {
  formData.value.formId = currentFair.value.formId;
  formData.value.token = token;
  formData.value.domain = window.location.origin;
  formData.value.pageUrl = window.location.pathname;
  formData.value.language = window.currentLanguage || storeConfig.caasConfig.language;
  formData.value.country = window.currentCountry || storeConfig.caasConfig.currentCountry;
  safeFetch(window.exhibitionFormUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData.value),
  })
    .then((response) => {
      if (response.status === 200) {
        window.open(props.config.thankyouUrl, '_self');
      } else {
        serverError.value = true;
      }
    })
    .catch((error) => {
      serverError.value = true;
      console.error(error);
    });
};

const getParameter = (param) => {
  var params = window.location.search.substring(1).split('&');

  for (var i = 0; i < params.length; i++) {
    var p = params[i].split('=');
    if (p[0] == param) {
      return decodeURIComponent(p[1]);
    }
  }
  return false;
};
</script>

<style module lang="scss">
@forward './OrderForm.scss';
</style>
